import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const baseFieldPack: FieldPack = {
  id: 'genogram-base',
  name: 'Base Genogram Fields',
  description: 'Core fields for all genogram diagrams',
  version: '1.0.0',
  fields: [
    {
      id: 'name',
      label: 'fields.name.label',
      description: 'fields.name.description',
      type: FieldType.TEXT,
      tags: ['identity', 'demographics'],
      priority: 100,
      required: true,
      placeholder: 'fields.name.placeholder',
      maxLength: 200,
    },
    {
      id: 'birthYear',
      label: 'fields.birthYear.label',
      description: 'fields.birthYear.description',
      type: FieldType.NUMBER,
      tags: ['identity', 'demographics', 'timeline'],
      priority: 95,
      placeholder: 'fields.birthYear.placeholder',
      min: 1800,
      max: new Date().getFullYear(),
    },
    {
      id: 'deathYear',
      label: 'fields.deathYear.label',
      description: 'fields.deathYear.description',
      type: FieldType.NUMBER,
      tags: ['identity', 'demographics', 'timeline'],
      priority: 90,
      placeholder: 'fields.deathYear.placeholder',
      min: 1800,
      max: new Date().getFullYear(),
    },
    {
      id: 'age',
      label: 'fields.age.label',
      description: 'fields.age.description',
      type: FieldType.NUMBER,
      tags: ['identity', 'demographics'],
      priority: 85,
      min: 0,
      max: 150,
    },
    {
      id: 'sex',
      label: 'fields.sex.label',
      description: 'fields.sex.description',
      type: FieldType.SELECT,
      tags: ['identity', 'demographics'],
      priority: 80,
      options: [
        { value: 'male', label: 'fields.sex.options.male' },
        { value: 'female', label: 'fields.sex.options.female' },
        { value: 'other', label: 'fields.sex.options.other' },
        { value: 'unknown', label: 'fields.sex.options.unknown' },
      ],
    },
    {
      id: 'gender',
      label: 'fields.gender.label',
      description: 'fields.gender.description',
      type: FieldType.TEXT,
      tags: ['identity', 'demographics'],
      priority: 75,
      placeholder: 'fields.gender.placeholder',
    },
    {
      id: 'occupation',
      label: 'fields.occupation.label',
      description: 'fields.occupation.description',
      type: FieldType.TEXT,
      tags: ['employment', 'professional'],
      priority: 70,
      placeholder: 'fields.occupation.placeholder',
    },
    {
      id: 'education',
      label: 'fields.education.label',
      description: 'fields.education.description',
      type: FieldType.SELECT,
      tags: ['education', 'professional'],
      priority: 65,
      options: [
        { value: 'elementary', label: 'fields.education.options.elementary' },
        { value: 'high_school', label: 'fields.education.options.high_school' },
        { value: 'some_college', label: 'fields.education.options.some_college' },
        { value: 'associates', label: 'fields.education.options.associates' },
        { value: 'bachelors', label: 'fields.education.options.bachelors' },
        { value: 'masters', label: 'fields.education.options.masters' },
        { value: 'doctorate', label: 'fields.education.options.doctorate' },
        { value: 'other', label: 'fields.education.options.other' },
      ],
    },
    {
      id: 'location',
      label: 'fields.location.label',
      description: 'fields.location.description',
      type: FieldType.TEXT,
      tags: ['contact', 'location'],
      priority: 60,
      placeholder: 'fields.location.placeholder',
    },
    {
      id: 'culturalHeritageTags',
      label: 'fields.culturalHeritageTags.label',
      description: 'fields.culturalHeritageTags.description',
      type: FieldType.MULTI_SELECT,
      tags: ['identity', 'cultural'],
      priority: 70,
      allowCustomOptions: true,
      options: [
        { value: 'african_american', label: 'fields.culturalHeritageTags.options.african_american' },
        { value: 'asian', label: 'fields.culturalHeritageTags.options.asian' },
        { value: 'caucasian', label: 'fields.culturalHeritageTags.options.caucasian' },
        { value: 'hispanic_latino', label: 'fields.culturalHeritageTags.options.hispanic_latino' },
        { value: 'native_american', label: 'fields.culturalHeritageTags.options.native_american' },
        { value: 'pacific_islander', label: 'fields.culturalHeritageTags.options.pacific_islander' },
        { value: 'middle_eastern', label: 'fields.culturalHeritageTags.options.middle_eastern' },
        { value: 'mixed', label: 'fields.culturalHeritageTags.options.mixed' },
      ],
    },
    {
      id: 'religion',
      label: 'fields.religion.label',
      description: 'fields.religion.description',
      type: FieldType.TEXT,
      tags: ['identity', 'cultural'],
      priority: 65,
      placeholder: 'fields.religion.placeholder',
    },
    {
      id: 'maritalStatus',
      label: 'fields.maritalStatus.label',
      description: 'fields.maritalStatus.description',
      type: FieldType.SELECT,
      tags: ['social', 'relationships'],
      priority: 45,
      options: [
        { value: 'single', label: 'fields.maritalStatus.options.single' },
        { value: 'married', label: 'fields.maritalStatus.options.married' },
        { value: 'divorced', label: 'fields.maritalStatus.options.divorced' },
        { value: 'widowed', label: 'fields.maritalStatus.options.widowed' },
        { value: 'separated', label: 'fields.maritalStatus.options.separated' },
        { value: 'domestic_partnership', label: 'fields.maritalStatus.options.domestic_partnership' },
      ],
    },
    {
      id: 'contactInfo',
      label: 'fields.contactInfo.label',
      description: 'fields.contactInfo.description',
      type: FieldType.TEXT,
      tags: ['contact', 'location'],
      priority: 40,
      multiline: true,
      placeholder: 'fields.contactInfo.placeholder',
    },
    {
      id: 'notes',
      label: 'fields.notes.label',
      description: 'fields.notes.description',
      type: FieldType.RICH_TEXT,
      tags: ['notes', 'documentation'],
      priority: 10,
      multiline: true,
      placeholder: 'fields.notes.placeholder',
    },
  ],
};
